import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, Home, LayoutDashboard, Plus, Send } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PublicShell, PraxisPublic } from "../components/PublicShell";
import { Button, Card, ErrorBox, Spinner } from "../components/kd";
import { StepPraxis, StepPatient, StepReferral, StepFiles, StepConfirm, validateStep, EMPTY } from "./referral/Steps";

const STEPS = ["Ihre Praxis", "Patient", "Überweisung", "Dateien", "Bestätigung"];

function Progress({ step }: { step: number }) {
  return (
    <ol className="mb-6 grid grid-cols-5 gap-1.5" aria-label="Fortschritt" data-testid="referral-progress">
      {STEPS.map((s, i) => (
        <li key={s} aria-current={i === step ? "step" : undefined}>
          <div className={`h-1.5 rounded-full ${i <= step ? "bg-primary" : "bg-border"}`} />
          <p className={`mt-1.5 hidden text-sm sm:block ${i === step ? "font-semibold text-primary" : "text-text-muted"}`}>{i + 1}. {s}</p>
        </li>
      ))}
      <li className="col-span-5 text-sm text-text-muted sm:hidden">Schritt {step + 1} von 5: {STEPS[step]}</li>
    </ol>
  );
}

function Success({ res, slug, konto, onNew }: { res: any; slug: string; konto: boolean; onNew: () => void }) {
  const sec = "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 font-medium text-text no-underline transition-colors hover:bg-bg";
  return (
    <Card className="kd-fade text-center" data-testid="referral-success">
      <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-success" aria-hidden />
      <h1 className="mb-2">Überweisung gesendet</h1>
      <p className="mb-1 text-text-muted">Ihre Fallnummer</p>
      <p className="mb-4 text-h2 font-semibold text-primary" data-testid="referral-fallnummer">{res.fallnummer}</p>
      <p className="mb-6">Die Praxis meldet sich beim Patienten. {res.email_versendet === false
        ? <span data-testid="referral-email-failed">Die Bestätigungs-E-Mail konnte gerade nicht gesendet werden – bitte notieren Sie die Fallnummer oder öffnen Sie die Statusseite.</span>
        : "Eine Bestätigung mit dem Link zur Statusseite haben wir Ihnen per E-Mail gesendet."}</p>
      <div className="mx-auto flex max-w-md flex-col gap-3">
        <a href={`/f/${res.zugriffs_token}`} className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-primary px-4 font-medium text-white no-underline hover:bg-primary-dark" data-testid="referral-status-link">
          <Eye className="h-4 w-4" aria-hidden />Zur Statusseite
        </a>
        <button type="button" onClick={onNew} className={sec} data-testid="referral-new-button"><Plus className="h-4 w-4 text-primary" aria-hidden />Neue Überweisung</button>
        {konto
          ? <Link to="/ueberweiser" className={sec} data-testid="referral-overview-link"><LayoutDashboard className="h-4 w-4 text-primary" aria-hidden />Zur Übersicht</Link>
          : <Link to="/" className={sec} data-testid="referral-home-link"><Home className="h-4 w-4 text-primary" aria-hidden />Startseite</Link>}
      </div>
      {!konto && <p className="mt-6 text-sm text-text-muted">Regelmäßig überweisen? <a href={`/registrieren?praxis=${slug}`} data-testid="referral-register-link">Konto anlegen</a> – alle Fälle an einem Ort.</p>}
    </Card>
  );
}

export default function ReferralForm() {
  const { slug } = useParams();
  const [praxis, setPraxis] = useState<PraxisPublic | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(0);
  const [data, setData] = useState<any>(EMPTY);
  const [verified, setVerified] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [konto, setKonto] = useState(false);

  useEffect(() => {
    api.get(`/public/praxis/${slug}`).then((r) => setPraxis(r.data)).catch(() => setNotFound(true));
    api.get(`/public/praxis/${slug}/sitzung`).then((r) => {
      if (r.data.angemeldet) {
        setVerified(r.data.email);
        setKonto(!!r.data.konto);
        if (r.data.ueberweiser) setData((d: any) => ({ ...d, ueberweiser: { ...d.ueberweiser, ...r.data.ueberweiser } }));
      }
    }).catch(() => undefined);
  }, [slug]);

  const set = (patch: any) => setData((d: any) => ({ ...d, ...patch }));
  const next = () => {
    const e = validateStep(step, data, verified, !!praxis?.testmodus);
    setErrors(e);
    if (Object.keys(e).length) return;
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setErrors({}); setStep(step - 1); window.scrollTo({ top: 0 }); };

  const submit = async () => {
    const e = validateStep(4, data, verified, !!praxis?.testmodus);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true); setErr("");
    try {
      const { ueberweiser, datei_ids, geburtsdatum_text, ...rest } = data;
      const { email, ...ueb } = ueberweiser;
      const body = { ...rest, ueberweiser: ueb, datei_ids, patient_email: rest.patient_email || null, grund_freitext: rest.grund_freitext || null, bemerkung: rest.bemerkung || null };
      const r = await api.post(`/public/praxis/${slug}/ueberweisung`, body);
      setResult(r.data);
      window.scrollTo({ top: 0 });
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };

  const startNew = () => {
    setData((d: any) => ({ ...EMPTY, ueberweiser: d.ueberweiser }));
    setResult(null); setErrors({}); setErr(""); setStep(verified ? 1 : 0);
    window.scrollTo({ top: 0 });
  };

  if (notFound) return <PublicShell><Card><h1>Praxis nicht gefunden</h1><p className="mt-2 text-text-muted">Bitte prüfen Sie den Link.</p></Card></PublicShell>;
  if (!praxis) return <PublicShell><Spinner /></PublicShell>;
  if (result) return <PublicShell praxis={praxis}><Success res={result} slug={slug as string} konto={konto} onNew={startNew} /></PublicShell>;

  const props = { data, set, errors, slug, verified, setVerified, setUploading, testmodus: !!praxis.testmodus };
  return (
    <PublicShell praxis={praxis}>
      <h1 className="mb-1">Patienten überweisen</h1>
      <p className="mb-6 text-text-muted">Digitale Überweisung an {praxis.name}</p>
      <Progress step={step} />
      <Card className="kd-fade" key={step}>
        <ErrorBox testid="referral-error">{err}</ErrorBox>
        {step === 0 && <StepPraxis {...props} />}
        {step === 1 && <StepPatient {...props} />}
        {step === 2 && <StepReferral {...props} />}
        {step === 3 && <StepFiles {...props} />}
        {step === 4 && <StepConfirm {...props} />}
        <div className={`mt-6 flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-between ${step === 0 && !verified ? "hidden" : "flex"}`}>
          {step > 0 ? <Button variant="secondary" onClick={back} data-testid="referral-back-button"><ArrowLeft className="h-4 w-4" aria-hidden />Zurück</Button> : <span />}
          {step < 4 && (step > 0 || verified) && (
            <Button onClick={next} disabled={uploading} data-testid="referral-next-button">Weiter<ArrowRight className="h-4 w-4" aria-hidden /></Button>
          )}
          {step === 4 && <Button onClick={submit} loading={busy} data-testid="referral-submit-button"><Send className="h-4 w-4" aria-hidden />Überweisung verbindlich senden</Button>}
        </div>
      </Card>
      <p className="mt-6 text-sm text-text-muted">
        Bereits überwiesen? <a href={`/meine-faelle?praxis=${slug}`} data-testid="my-cases-link">Ihre Fälle ansehen</a>
      </p>
    </PublicShell>
  );
}
